import React from 'react';
import { Popover } from '@brighterion/br-ui';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getFullStringCondition } from 'Utils';
import { shouldShowCreateRule } from 'Selectors';
import { INPUT_PARAMETER_TYPES, OPERATOR_CONDITIONS } from 'Common/enums';
import { FormikFieldFreeEntry, FormikFieldString } from 'Components/FormikComponents';
import { HandleBlur, HandleChange, SetFieldValue } from 'Components/FormikComponents/types';
import _ from 'lodash';
import ListDropdown from './ListDropdown';
import styles from './Condition.css';

type CommonProps = {
    input: RuleInputs;
    error: any;
    errorMsg: string;
    isSubmitting: boolean;
    formikValues: TemplatedRuleFormValues;
    initialValues: TemplatedRuleFormValues;
    setFieldValue: SetFieldValue;
    handleChange: HandleChange;
    handleBlur: HandleBlur;
    listData?: ListApiGetResponse;
};

type ConditionProps = {
    index: number;
    parameterData: UseGetParametersResponse | undefined;
} & CommonProps;

type GetInputFieldOfCurrentInputTypeProps = {
    t: TFunction;
    parameterData: UseGetParametersResponse | undefined;
} & CommonProps;

const getInputFieldOfCurrentInputType = ({
    input,
    error,
    errorMsg,
    isSubmitting,
    formikValues,
    initialValues,
    t,
    setFieldValue,
    handleChange,
    handleBlur,
    listData,
    parameterData
}: GetInputFieldOfCurrentInputTypeProps) => {
    const isReadOnly = input.readOnly;
    const invalidListIndex = input.parameter.listIndex == null || _.isNaN(input.parameter.listIndex);

    switch (input.condition) {
        // Text or Numeric Field
        case OPERATOR_CONDITIONS.EQ:
        case OPERATOR_CONDITIONS.NEQ:
        case OPERATOR_CONDITIONS.LT:
        case OPERATOR_CONDITIONS.LTEQ:
        case OPERATOR_CONDITIONS.GT:
        case OPERATOR_CONDITIONS.GTEQ:
        case OPERATOR_CONDITIONS.STR_EQ:
        case OPERATOR_CONDITIONS.STR_NEQ:
        case OPERATOR_CONDITIONS.STR_CONTAINS:
        case OPERATOR_CONDITIONS.STR_NOT_CONTAINS:
            if (isReadOnly) return <div className={styles.readOnlyValue}>{initialValues[input.parameter.name]}</div>;

            return (
                <FormikFieldString
                    dataTestId={`${input.parameter.name}-${input.parameter.type}-field`}
                    id={input.parameter.name}
                    name={input.parameter.name}
                    className={styles.conditionValueInput}
                    type={input.parameter.type === INPUT_PARAMETER_TYPES.STRING ? 'text' : 'number'}
                    error={error && !isReadOnly}
                    errorMsg={errorMsg}
                    disabled={isSubmitting}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                />
            );

        // FreeEntry Field
        case OPERATOR_CONDITIONS.STR_IN:
        case OPERATOR_CONDITIONS.STR_NOT_IN:
            if (isReadOnly) return <div className={styles.readOnlyValue}>{initialValues[input.parameter.name]}</div>;

            return (
                <FormikFieldFreeEntry
                    dataTestId={`${input.parameter.name}-${input.parameter.type}-field`}
                    name={input.parameter.name}
                    maxLength={50}
                    className={styles.conditionValueInput}
                    error={error && !isReadOnly}
                    errorMsg={errorMsg}
                    disabled={isSubmitting}
                />
            );

        // ListDropdown Field
        case OPERATOR_CONDITIONS.STR_IN_LIST:
        case OPERATOR_CONDITIONS.STR_NOT_IN_LIST:
            if (!formikValues.lists || invalidListIndex) {
                return <span>{t('No Lists Found')}</span>;
            }

            // If a rule condition is not read only and a tenant overrides it, we want to reset the
            // landlord list so that the tenant is forced to apply a tenant list
            // we can assert this by checking if the tenant's lists includes the condition's listIds
            if (!isReadOnly) {
                let allListIndicesExist = true;
                formikValues.lists[Number(input.parameter.listIndex)].listIds.forEach((listId: any) => {
                    if (!listData?.listIdHashMap[listId]) {
                        allListIndicesExist = false;
                        return false;
                    }
                    allListIndicesExist = true;
                    return true;
                });
                if (!allListIndicesExist) {
                    formikValues.lists[Number(input.parameter.listIndex)].listIds = [];
                }
            }
            return (
                formikValues.lists && (
                    <ListDropdown
                        formikList={formikValues.lists[Number(input.parameter.listIndex)]}
                        setFieldValue={setFieldValue}
                        listError={error[Number(input.parameter.listIndex)] && !isReadOnly}
                        listErrorMsg={errorMsg[Number(input.parameter.listIndex)]}
                        listIndex={Number(input.parameter.listIndex)}
                        disabled={isSubmitting}
                        isReadOnly={isReadOnly}
                        listData={listData}
                        parameterType={parameterData?.fieldNameHashmap[input.fieldName]?.type}
                    />
                )
            );

        default:
            return <span className={styles.nomatchingFieldPrompt}>{t('No Matching Input Field Found')}</span>;
    }
};

const Condition = ({
    input,
    index,
    error,
    errorMsg,
    isSubmitting,
    formikValues,
    initialValues,
    parameterData,
    setFieldValue,
    handleChange,
    handleBlur,
    listData
}: ConditionProps) => {
    const { t } = useTranslation();
    const IsCreateRuleAllowed = shouldShowCreateRule();

    return (
        <div className={styles.conditionalStatementTextBar}>
            <div className={styles.conditionColumn}>
                <span className={styles.statementSideLabel}>{index === 0 ? t('WHEN') : t('AND')}</span>
            </div>

            <div className={styles.conditionColumn}>
                <Popover
                    showOnHover
                    showArrow
                    position='right-end'
                    popperClassName={styles.popOverStyle}
                    contentClassName={
                        IsCreateRuleAllowed ? styles.popOverFullContentStyle : styles.popOverMinimalContentStyle
                    }
                    arrowClassName={styles.popperArrowStyle}
                    content={
                        <table className={styles.popOverTable}>
                            <tbody>
                                <tr>
                                    <td>System Name</td>
                                    <td>
                                        {IsCreateRuleAllowed
                                            ? parameterData?.fieldNameHashmap[input.fieldName]?.fieldName ?? '_'
                                            : input.fieldName ?? '_'}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Display Name</td>
                                    <td>
                                        {IsCreateRuleAllowed
                                            ? parameterData?.fieldNameHashmap[input.fieldName]?.message ?? '_'
                                            : input.message ?? '_'}
                                    </td>
                                </tr>

                                {IsCreateRuleAllowed && (
                                    <>
                                        <tr>
                                            <td>Type</td>
                                            <td>{parameterData?.fieldNameHashmap[input.fieldName]?.type ?? '_'}</td>
                                        </tr>
                                        <tr>
                                            <td>Description</td>
                                            <td>
                                                {parameterData?.fieldNameHashmap[input.fieldName]?.description ?? '_'}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    }
                >
                    <div className={styles.readOnly}>{input?.message ?? input?.fieldName ?? '_'}</div>
                </Popover>
            </div>

            <div className={styles.conditionColumn}>
                <div className={styles.readOnly}>{getFullStringCondition(t, input.condition)}</div>
            </div>

            <div className={styles.conditionColumn}>
                {getInputFieldOfCurrentInputType({
                    input,
                    error,
                    errorMsg,
                    isSubmitting,
                    formikValues,
                    initialValues,
                    t,
                    setFieldValue,
                    handleChange,
                    handleBlur,
                    listData,
                    parameterData
                })}
            </div>
        </div>
    );
};

export default React.memo(Condition);
